import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../config";
import { PageLoader } from '../components/ui';

const UploadAccountStatement = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState({ name: '', customerUniqueId: '' });
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
  }, []);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/personal_details`);
      console.log('Full Customer Data:', response.data);
      const customerData = response.data?.data || response.data;
      setCustomers(Array.isArray(customerData) ? customerData : []);
      setFilteredCustomers(Array.isArray(customerData) ? customerData : []);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      setError('Failed to load customer data');
      setCustomers([]);
      setFilteredCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (customerUniqueId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/transactions`, {
        params: { customerUniqueId }
      });
      console.log(`Transactions for ${customerUniqueId}:`, response.data);
      setTransactions(Array.isArray(response.data) ? response.data : []);
      setSelectedCustomerId(customerUniqueId);
      setShowPopup(true);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const filtered = customers.filter((customer) => {
      const matchesName = customer.name?.toLowerCase().includes(searchCriteria.name.toLowerCase()) || false;
      const matchesId = customer.customerUniqueId?.toLowerCase().includes(searchCriteria.customerUniqueId.toLowerCase()) || false;
      return matchesName && matchesId;
    });
    setFilteredCustomers(filtered);
  }, [searchCriteria, customers]);

  const closePopup = () => {
    setShowPopup(false);
    setTransactions([]);
  };

  if (loading) return <PageLoader />;
  if (error) return <div className="p-6 text-center text-danger-500 font-medium">{error}</div>;

  return (
    <div className="content-area">
      <div className="page-header">
        <h2 className="text-page-title">Upload Account Statement</h2>
      </div>

      <div className="filter-bar">
        <div className="flex gap-4 w-full sm:w-auto">
          <input
            type="text"
            name="name"
            className="form-input max-w-xs"
            placeholder="Search by Name"
            value={searchCriteria.name}
            onChange={handleSearchChange}
          />
          <input
            type="text"
            name="customerUniqueId"
            className="form-input max-w-xs"
            placeholder="Search by Customer ID"
            value={searchCriteria.customerUniqueId}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="section mt-6">
        <h3 className="text-section-title mb-4">Customers List</h3>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer Unique ID</th>
                <th>Name</th>
                <th>Gender</th>
                <th>DOB</th>
                <th>Field Officer ID</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-ink-300">
                    No customers found matching the search criteria.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td>{customer.id || 'N/A'}</td>
                    <td className="font-semibold text-primary-500">{customer.customerUniqueId || 'N/A'}</td>
                    <td className="font-medium">{customer.name || 'N/A'}</td>
                    <td>{customer.gender || 'N/A'}</td>
                    <td>{customer.dob || 'N/A'}</td>
                    <td>{customer.fieldOfficerId || 'N/A'}</td>
                    <td className="text-right">
                      <button className="btn-primary btn-sm" onClick={() => fetchTransactions(customer.customerUniqueId)}>
                        Transactions
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPopup && (
        <div className="loader-overlay">
          <div className="card max-w-5xl w-full mx-4 shadow-xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="card-header flex justify-between items-center bg-surface-base border-b border-border">
              <h3 className="text-section-title">Transactions for {selectedCustomerId}</h3>
              <button className="btn-ghost btn-icon-sm" onClick={closePopup}>
                <span className="text-xl font-bold">&times;</span>
              </button>
            </div>
            <div className="card-body overflow-auto flex-1 p-0">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Transaction ID</th>
                    <th className="text-right">Amount</th>
                    <th>Transaction Type</th>
                    <th>Account Type</th>
                    <th>Account Number</th>
                    <th>Customer Unique ID</th>
                    <th>Narration</th>
                    <th>Date</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-8 text-ink-300">
                        No transactions found.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction) => (
                      <tr key={transaction.transactionId}>
                        <td>{transaction.id || 'N/A'}</td>
                        <td className="font-medium">{transaction.transactionId || 'N/A'}</td>
                        <td className="text-right font-semibold text-ink-900 tabular-nums">
                          {transaction.amount || 'N/A'}
                        </td>
                        <td>
                          <span className={`badge ${transaction.transactionType?.toLowerCase() === 'credit' ? 'badge-success' : 'badge-danger'}`}>
                            {transaction.transactionType || 'N/A'}
                          </span>
                        </td>
                        <td>{transaction.accountType || 'N/A'}</td>
                        <td className="font-mono text-xs">{transaction.accountNumber || 'N/A'}</td>
                        <td>{transaction.customerUniqueId || 'N/A'}</td>
                        <td className="max-w-xs truncate">{transaction.narration || 'N/A'}</td>
                        <td>{transaction.date || 'N/A'}</td>
                        <td className="text-xs text-ink-300">{transaction.createdAt || 'N/A'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-footer bg-surface-base border-t border-border flex justify-end gap-3">
              <button className="btn-secondary btn-sm" onClick={closePopup}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadAccountStatement;
